namespace Solovis.Reports.Parameters.AttributionReport {

  export interface AttributionPortfolioValue {
    entityId?: number;
    investmentId?: number;
    resourceId?: number;
  }

  export interface AttributionGroupByValue {
    id?: number;
    label?: string;
  }

  /**
   * Builds the Custom CompositeSelectionSetData that IntervalStatsDataSetLoader expects,
   * from the Attribution Report's existing "portfolio", "groupBy", and "otherOptions"
   * parameters.
   *
   * IntervalStatsDataSetLoader.Composite requires exactly one PropertyValue grouping
   * declaration and CreateOtherGroups = true for attribution to compute at all (both
   * enforced server-side, InvData.CoreDataSets/IntervalStats/IntervalStatsDataSetLoader.Composite.cs)
   * -- there is no such thing as an "ungrouped" attribution composite. Shared by both
   * AttributionReport.js data sources: the attribution table (dataSourceForAttributionTable)
   * renders one row per composite node under this grouping, so groupBy directly determines
   * the row breakdown, not just the "All" aggregate. The KPI tiles (dataSourceForKpiTiles)
   * only ever read the top-level "All" row, so the choice of grouping property doesn't
   * affect their values -- the "All" aggregate is the same regardless of how the breakdown
   * beneath it is organized -- but groupBy is still required here since the server-side
   * grouping requirement applies regardless of which consumer is calling. Both callers treat
   * a missing groupBy the same as a missing portfolio/time period and skip loading rather
   * than calling this with none.
   */
  export function buildAttributionCompositeSelectionSetRef(
    portfolio: AttributionPortfolioValue,
    groupBy: AttributionGroupByValue,
    otherOptions?: AttributionOtherOptions
  ): { custom: Solovis.Reports.Parameters.Composites.CompositeSelectionSetData } {

    const custom: Solovis.Reports.Parameters.Composites.CompositeSelectionSetData = {
      removeEmptyComponents: true,
      createOtherGroups: true,
      groupingDeclarations: [
        { declarationType: "PropertyValue", propertyDefinitionId: groupBy.id, label: groupBy.label }
      ]
    };

    if (portfolio) {
      if (portfolio.entityId) {
        custom.ownerEntityIds = [portfolio.entityId];
      }
      else if (portfolio.investmentId) {
        custom.investmentIds = [portfolio.investmentId];
      }
    }

    const configurations = otherOptions && otherOptions.configurations;
    if (configurations) {
      if (typeof configurations.removeEmptyComponents === "boolean") {
        custom.removeEmptyComponents = configurations.removeEmptyComponents;
      }
      if (configurations.allLabel) {
        custom.allLabel = configurations.allLabel;
      }
    }

    return { custom: custom };
  }
}
